import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FeatureFlag } from '@app/enums';
import { ProductPlan, StripeTier } from '@app/models/settings/billing/product-plan.model';
import { SelfServePlan } from '@app/modules/self-serve/types/plans.types';
import { AuthService } from '@app/services/auth.service';
import { FeatureService } from '@app/services/feature.service';
import { environment } from '@env/environment';
import { Product } from '@models/settings/billing/product.model';
import { SubscriptionItem } from '@models/settings/billing/subscription-item.model';
import { Subscription } from '@models/settings/billing/subscription.model';
import * as Sentry from '@sentry/angular';

@Component({
    selector: 'app-subscription-overview',
    templateUrl: './subscription-overview.template.html',
    styleUrls: ['./subscription-overview.style.scss'],
})
export class SubscriptionOverviewComponent {
    @Input() set subscriptions(subscriptions: Subscription[]) {
        this._subscriptions = subscriptions;
        if (subscriptions.length > 0 && this.isSelfServeCustomer) {
            this.setSelfServePricingInfo();
        }
    }
    get subscriptions(): Subscription[] {
        return this._subscriptions;
    }
    @Input() products: Product[] = [];

    @Output() cancel: EventEmitter<SubscriptionItem> = new EventEmitter();
    @Output() edit: EventEmitter<null> = new EventEmitter();

    invalidSelfServeConfiguration = false;
    isSelfServeModulesFlagEnabled = false;

    selfServePricingInfo: { flatTotal: number; tieredPricing: StripeTier[]; isLoading: boolean } = {
        flatTotal: 0,
        tieredPricing: [],
        isLoading: true,
    };

    private _subscriptions: Subscription[] = [];

    get isSelfServeCustomer(): boolean {
        return this.auth.company.isSelfServe;
    }

    get selfServePlan(): SelfServePlan | null {
        return this.auth.company.selfServePlan;
    }

    get isLoading(): boolean {
        if (!(this.subscriptions.length && this.products.length && this.defaultSubscriptionHasSubscriptionItems())) {
            return true;
        }

        if (this.isSelfServeCustomer && this.selfServePricingInfo.isLoading) {
            return true;
        }

        return false;
    }

    constructor(
        public auth: AuthService,
        private featureService: FeatureService
    ) {
        this.featureService
            .has(FeatureFlag.selfServeModules)
            .then((isSelfServeEnabled) => (this.isSelfServeModulesFlagEnabled = isSelfServeEnabled));
    }

    defaultSubscriptionHasSubscriptionItems(): boolean {
        if (!this.subscriptions.length) {
            return false;
        }

        if (!this.subscriptions[0].subscriptionItems.length) {
            return false;
        }

        return true;
    }

    productById(id: string): Product | undefined {
        return this.products.find((product) => product.identifier === id);
    }

    private setSelfServePricingInfo(): void {
        if (this.selfServePlan) {
            const subscriptionPlans = this.getSubscriptionPlans(this.selfServePlan);

            // If the plan was not found, then there is a data mismatch on the BE. We show nothing and report to Sentry
            if (subscriptionPlans.length === 0) {
                this.invalidSelfServeConfiguration = true;
                Sentry.captureMessage(
                    `Company self serve plan ${this.selfServePlan} not present in Subscriptions`,
                    'warning'
                );
                this.selfServePricingInfo.isLoading = false;
                return;
            }

            // Reset before calculating
            this.selfServePricingInfo = { flatTotal: 0, tieredPricing: [], isLoading: true };

            // There SHOULD only be one plan, but we loop through as a future-proof measure
            subscriptionPlans.forEach((plan) => {
                // Per unit pricing is a flat amount so it is added to the flat total
                if (plan.billingScheme === 'per_unit') {
                    this.selfServePricingInfo.flatTotal += plan.amount;
                }
                if (plan.billingScheme === 'tiered') {
                    this.calculateTieredPricing(plan.tiers);
                }
            });
            this.selfServePricingInfo.isLoading = false;
        }
    }

    private getSubscriptionPlans(selfServePlan: SelfServePlan): ProductPlan[] {
        // Using the user's plan type find the stripe ID in the environment files
        const stripePriceId = environment.stripeProducts?.selfServe?.[selfServePlan];

        // Look for the above stripe ID in the user's subscriptions
        const subscriptionPlans = this.subscriptions
            .flatMap((subscription) => subscription.subscriptionItems.map((item) => item.plan))
            .filter((plan) => plan.id === stripePriceId);

        return subscriptionPlans;
    }

    private calculateTieredPricing(tiers: StripeTier[]): void {
        let previousTierLimit = 0;
        // Always sort the tiers first to ensure they are in order of employee maximums
        tiers.sort(this.sortTiers).forEach(({ amount, flatAmount, upTo }, index) => {
            // First tier is always for 0 or greater employees and will always be a flat total
            // ie. $100 up to 10 employees, the $100 is put in flatTotal
            if (index === 0) {
                this.selfServePricingInfo.flatTotal += flatAmount ?? 0;
            } else {
                // For all tiers beyond the first, we show a disclaimer mentioning this additional cost
                // ie.  $13 per employee per month after 10 employees
                // The dollar amount and employee maximum are stored in an array here to be displayed
                this.selfServePricingInfo.tieredPricing.push({
                    upTo: previousTierLimit,
                    amount,
                    flatAmount,
                });
            }

            // Keep track of the previous tier limit to apply to the next tier
            // ie. if this tier is {upTo: 10} then we need that value for the messaging on the next tier, since it
            // will say "after 10 employees"
            previousTierLimit = upTo ?? 0;
        });
    }

    // Tiers should be sorted by Stripe but this is a fail-safe
    // it should always start with the lowest "upTo" value and end with a null "upTo" value
    // ie. [{upTo:10}, {upTo:20}, {upTo:30}, {upTo:null}]
    private sortTiers(tierA: StripeTier, tierB: StripeTier): number {
        if (tierA.upTo === null) {
            return 1;
        }
        if (tierB.upTo === null) {
            return -1;
        }
        return tierA.upTo - tierB.upTo;
    }
}
